import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button, PanelCard, TextLink } from '../../components/ui';
import NoticeComposer from '../../../pages/SchoolTasks/components/NoticeComposer';
import { initialsOf } from '../../components/ui/Initials';
import { useSectionsList, useStudentsList, useTeacherAssignments } from '../../api/hooks';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { genderSplit, homeroomInfo, subjectTeachers } from './sectionMath';

interface Props { gradeId: number; sectionId: number; studentCount: number }

const LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

/** Design SD "Section team": homeroom hero, roster / girls / boys tiles and the subject teachers. */
export default function SectionTeam({ gradeId, sectionId, studentCount }: Props) {
  const { role } = useAuth();
  const sections = useSectionsList(gradeId);
  // The assignments endpoint is admin-only: other roles never ask for it (no 403, no retries).
  const isAdminRole = role === 'school_admin' || role === 'school' || role === 'admin';
  const assignments = useTeacherAssignments({ enabled: isAdminRole, retry: false });
  const roster = useStudentsList({ grade: gradeId, section: sectionId });

  const homeroom = sections.data?.sections.find((s) => s.id === sectionId)?.homeroom_teacher ?? null;
  const homeroomName = homeroom ? `${homeroom.first_name ?? ''} ${homeroom.last_name ?? ''}`.trim() || homeroom.username : null;
  const list = assignments.data ?? [];
  // The assignments endpoint is admin-only; for other roles the subject-teacher block is simply left out.
  const teachers = assignments.data ? subjectTeachers(list, gradeId, sectionId) : [];
  const info = homeroom ? homeroomInfo(list, homeroom.id, gradeId, sectionId) : { subject: null, since: null };
  const split = roster.data ? genderSplit(roster.data.results) : null;
  const canOpenTeachers = isAdminRole;
  // Same roles that can send a notice from the Students page.
  const canMessage = isAdminRole || role === 'teacher';
  const [composing, setComposing] = useState(false);

  return (
    <PanelCard title="Section team" aria-label="Section team"
      actions={canOpenTeachers ? <TextLink to="/school-setup/teachers">Teachers →</TextLink> : undefined}>
      <div className="-mt-1.5 flex items-center gap-3.5">
        <span aria-hidden className="grid h-14 w-14 flex-none place-items-center rounded-[16px] bg-mist font-display text-lg font-bold text-ink">
          {homeroomName ? initialsOf(homeroomName) : '?'}
        </span>
        <span className="min-w-0 flex-1">
          <span className={LABEL}>Homeroom teacher</span>
          <span className="mt-1 block font-display text-lg font-bold">{homeroomName ?? 'Not assigned'}</span>
          {homeroomName && (
            <span className="text-[13px] text-muted">
              {[info.subject, info.since ? `homeroom since ${info.since}` : 'homeroom'].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
        {canMessage && (
          <Button variant="ghost" size="sm" icon={<Mail size={16} strokeWidth={1.8} aria-hidden />} onClick={() => setComposing(true)}>Message</Button>
        )}
      </div>
      {composing && (
        <NoticeComposer isOpen onClose={() => setComposing(false)} isAdmin={isAdminRole}
          initial={{ mode: 'sections', gradeId, sectionIds: [sectionId] }} />
      )}
      <div className={`my-[18px] grid gap-2.5 ${split ? 'grid-cols-3' : 'grid-cols-1'}`}>
        <div className="rounded-[12px] bg-panel px-3 py-2.5"><span className={LABEL}>Roster</span><b className="mt-0.5 block font-mono text-xl font-semibold">{studentCount}</b></div>
        {split && (
          <>
            <div className="rounded-[12px] bg-panel px-3 py-2.5"><span className={LABEL}>Girls</span><b className="mt-0.5 block font-mono text-xl font-semibold">{split.girls}</b></div>
            <div className="rounded-[12px] bg-panel px-3 py-2.5"><span className={LABEL}>Boys</span><b className="mt-0.5 block font-mono text-xl font-semibold">{split.boys}</b></div>
          </>
        )}
      </div>
      {teachers.length > 0 && (
        <>
          <p className={`${LABEL} mb-1`}>Subject teachers · {teachers.length}</p>
          <ul className="grid grid-cols-1 gap-x-[18px] gap-y-0.5 min-[641px]:grid-cols-2">
            {teachers.map((t) => (
              <li key={t.id} className="flex min-w-0 items-center gap-2.5 border-b border-hairline py-2">
                <span aria-hidden className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[10px] bg-track text-[11px] font-semibold text-muted-2">{initialsOf(t.name)}</span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-semibold">{t.name}</span>
                  <span className="text-xs text-muted">{t.subject}{t.isHomeroom ? ' · homeroom' : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </PanelCard>
  );
}
